import { useEffect, useMemo, useState } from "react"
import Select from "react-select"
import { Button, Input, Label, Modal, ModalBody, ModalHeader, Table } from "reactstrap"

import { useEmployeeBills } from "hooks/useEmployeeBill"
import { formatCurrency } from "utils/formatCurrency"
import { formatReadableDate } from "utils/formatDate"

const selectStyles = {
  control: (base, state) => Object.assign({}, base, {
    backgroundColor: state.isDisabled ? "#e9ecef" : "#fff",
    borderColor: state.isFocused ? "#80bdff" : "#d1d5db",
    boxShadow: state.isFocused ? "0 0 0 0.15rem rgba(0,123,255,0.25)" : "none",
    minHeight: "38px",
  }),
  menu: base => Object.assign({}, base, { zIndex: 9999 }),
}

const todayDate = () => {
  const d = new Date()
  const pad = n => String(n).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

const toOptions = items => (items || []).map(item => ({ label: item.name, value: item.id }))
const findOption = (options, value) => options.find(option => String(option.value) === String(value)) || null

const remainingOf = bill =>
  bill.remaining_amount != null
    ? Number(bill.remaining_amount)
    : Math.max(0, Number(bill.amount || 0) - Number(bill.paid_amount || 0) - Number(bill.adjusted_amount || 0))

const isOutstanding = bill =>
  ["pending", "partially_paid"].includes(bill && bill.status) && remainingOf(bill) > 0

const sortFifo = (a, b) => {
  const da = a.bill_date || a.created_at || ""
  const db = b.bill_date || b.created_at || ""
  if (da !== db) return da < db ? -1 : 1
  return Number(a.id) - Number(b.id)
}

const listFrom = response =>
  (response && response.data && response.data.data) || (response && response.data) || []

const BulkBillPaymentModal = ({
  isOpen,
  onClose,
  onSubmitted,
  loading,
  submit,
  employees = [],
  paymentFroms = [],
  paymentModes = [],
  defaultEmployeeId = "",
}) => {
  const [employeeId, setEmployeeId] = useState("")
  const [selectedIds, setSelectedIds] = useState([])
  const [paidAmount, setPaidAmount] = useState("")
  const [paymentDate, setPaymentDate] = useState(todayDate())
  const [paymentFromId, setPaymentFromId] = useState("")
  const [paymentModeId, setPaymentModeId] = useState("")
  const [notes, setNotes] = useState("")

  const employeeOptions = useMemo(() => toOptions(employees), [employees])
  const fromOptions = useMemo(() => toOptions(paymentFroms), [paymentFroms])
  const modeOptions = useMemo(() => toOptions(paymentModes), [paymentModes])

  const billsQuery = useEmployeeBills(
    { page: 1, per_page: 100, employee_id: employeeId },
    isOpen && !!employeeId
  )

  const outstandingBills = useMemo(
    () => listFrom(billsQuery.data).filter(isOutstanding).sort(sortFifo),
    [billsQuery.data]
  )

  useEffect(() => {
    if (!isOpen) return
    setEmployeeId(defaultEmployeeId ? String(defaultEmployeeId) : "")
    setSelectedIds([])
    setPaidAmount("")
    setPaymentDate(todayDate())
    setPaymentFromId("")
    setPaymentModeId("")
    setNotes("")
  }, [isOpen, defaultEmployeeId])

  useEffect(() => {
    setSelectedIds(outstandingBills.map(bill => bill.id))
  }, [outstandingBills])

  const selectedBills = useMemo(
    () => outstandingBills.filter(bill => selectedIds.includes(bill.id)),
    [outstandingBills, selectedIds]
  )

  const totalSelectedDue = useMemo(
    () => selectedBills.reduce((sum, bill) => sum + remainingOf(bill), 0),
    [selectedBills]
  )

  const allocation = useMemo(() => {
    let leftover = Number(paidAmount || 0)
    const rows = selectedBills.map(bill => {
      const due = remainingOf(bill)
      const applied = Math.max(0, Math.min(due, leftover))
      leftover = Math.round((leftover - applied) * 100) / 100
      return { bill, due, applied }
    })
    return { rows, advance: Math.round(leftover * 100) / 100 }
  }, [selectedBills, paidAmount])

  const toggleBill = id =>
    setSelectedIds(prev => (prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]))

  const canSubmit =
    !!employeeId && Number(paidAmount) > 0 && !!paymentFromId && !!paymentModeId && !loading

  const handleSubmit = () => {
    submit({
      employee_id: employeeId,
      paid_amount: paidAmount,
      payment_date: paymentDate || undefined,
      payment_from_id: paymentFromId,
      payment_mode_id: paymentModeId,
      bill_ids: selectedIds,
      notes: notes || undefined,
    })
      .then(() => {
        onClose()
        if (onSubmitted) onSubmitted()
      })
      .catch(() => {})
  }

  return (
    <Modal isOpen={isOpen} toggle={onClose} centered size="lg">
      <ModalHeader toggle={onClose}>Bulk Bill Payment</ModalHeader>
      <ModalBody>
        <Label>Employee</Label>
        <Select
          styles={selectStyles}
          options={employeeOptions}
          value={findOption(employeeOptions, employeeId)}
          onChange={option => setEmployeeId(option ? String(option.value) : "")}
          placeholder="Select employee"
          isClearable
        />

        {employeeId && (
          <>
            <div className="d-flex justify-content-between align-items-center mt-3">
              <Label className="mb-0">Outstanding Bills</Label>
              <span className="text-muted small">
                Selected due: {formatCurrency(totalSelectedDue, "BDT")}
              </span>
            </div>
            {billsQuery.isLoading ? (
              <p className="text-muted small mt-2">Loading bills...</p>
            ) : outstandingBills.length === 0 ? (
              <p className="text-muted small mt-2">No outstanding bills for this employee.</p>
            ) : (
              <Table responsive borderless className="mb-0">
                <thead>
                  <tr className="text-muted small">
                    <th />
                    <th>Bill</th>
                    <th>Date</th>
                    <th>Due</th>
                    <th>Applied</th>
                  </tr>
                </thead>
                <tbody>
                  {allocation.rows.map(({ bill, due, applied }) => (
                    <tr key={bill.id}>
                      <td>
                        <Input
                          type="checkbox"
                          checked={selectedIds.includes(bill.id)}
                          onChange={() => toggleBill(bill.id)}
                        />
                      </td>
                      <td>
                        #{bill.id}
                        {bill.payment_for && bill.payment_for.name ? ` · ${bill.payment_for.name}` : ""}
                      </td>
                      <td>{bill.bill_date ? formatReadableDate(bill.bill_date) : "-"}</td>
                      <td>{formatCurrency(due, "BDT")}</td>
                      <td className="fw-semibold">{formatCurrency(applied, "BDT")}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </>
        )}

        <Label className="mt-3">Paid Amount</Label>
        <Input
          type="number"
          min="0"
          step="0.01"
          value={paidAmount}
          onChange={e => setPaidAmount(e.target.value)}
        />
        {Number(paidAmount) > 0 && (
          <p className="text-muted small mt-1">
            Applies {formatCurrency(Number(paidAmount) - allocation.advance, "BDT")} to bills
            {allocation.advance > 0
              ? `, ${formatCurrency(allocation.advance, "BDT")} added as advance.`
              : "."}
          </p>
        )}

        <Label className="mt-3">Payment Date</Label>
        <Input type="date" value={paymentDate} onChange={e => setPaymentDate(e.target.value)} />

        <Label className="mt-3">Payment From</Label>
        <Select
          styles={selectStyles}
          options={fromOptions}
          value={findOption(fromOptions, paymentFromId)}
          onChange={option => setPaymentFromId(option ? String(option.value) : "")}
          placeholder="Select payment source"
        />

        <Label className="mt-3">Payment Mode</Label>
        <Select
          styles={selectStyles}
          options={modeOptions}
          value={findOption(modeOptions, paymentModeId)}
          onChange={option => setPaymentModeId(option ? String(option.value) : "")}
          placeholder="Select payment mode"
        />

        <Label className="mt-3">Notes</Label>
        <Input type="textarea" value={notes} onChange={e => setNotes(e.target.value)} />

        <div className="text-end mt-3">
          <Button color="success" disabled={!canSubmit} onClick={handleSubmit}>
            {loading ? "Recording..." : "Record Bulk Payment"}
          </Button>
        </div>
      </ModalBody>
    </Modal>
  )
}

export default BulkBillPaymentModal
